import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Schedule as ScheduleIcon,
  AccessTime as TimeIcon,
  Phone as PhoneIcon,
  Tv as TvIcon,
  FamilyRestroom as FamilyIcon,
  School as SchoolIcon,
  SportsEsports as GamingIcon,
  Restaurant as FoodIcon,
  Bedtime as SleepIcon
} from '@mui/icons-material';

const RoutineCollection = ({ onComplete, onBack }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [routine, setRoutine] = useState({
    wakeUpTime: '07:00',
    sleepTime: '22:00',
    schoolHours: { start: '08:00', end: '15:00' },
    mobileUsage: 2, // hours per day
    tvTime: 1, // hours per day
    familyTime: 1, // hours per day
    gamingTime: 0.5, // hours per day
    mealTimes: {
      breakfast: '07:30',
      lunch: '12:30',
      dinner: '19:00'
    },
    preferredStudyTimes: ['morning', 'evening'],
    studyBreaks: 15, // minutes
    subjects: []
  });

  const steps = [
    {
      title: "Daily Schedule",
      description: "Let's start with your basic daily routine",
      icon: <ScheduleIcon className="w-6 h-6" />
    },
    {
      title: "Study Preferences", 
      description: "Tell us about your study habits and preferences",
      icon: <SchoolIcon className="w-6 h-6" />
    },
    {
      title: "Time Management",
      description: "How do you spend your free time?",
      icon: <TimeIcon className="w-6 h-6" />
    },
    {
      title: "Review & Confirm",
      description: "Review your routine and confirm",
      icon: <ScheduleIcon className="w-6 h-6" />
    }
  ];

  const handleInputChange = (field, value) => {
    setRoutine(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNestedChange = (parent, field, value) => {
    setRoutine(prev => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [field]: value
      }
    }));
  };

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    onComplete(routine);
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2">
                  <SleepIcon className="w-5 h-5 text-blue-500" />
                  Sleep Schedule
                </h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Wake Up Time
                    </label>
                    <input
                      type="time"
                      value={routine.wakeUpTime}
                      onChange={(e) => handleInputChange('wakeUpTime', e.target.value)}
                      className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Sleep Time
                    </label>
                    <input
                      type="time"
                      value={routine.sleepTime}
                      onChange={(e) => handleInputChange('sleepTime', e.target.value)}
                      className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2">
                  <SchoolIcon className="w-5 h-5 text-green-500" />
                  School Hours
                </h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      School Start Time
                    </label>
                    <input
                      type="time"
                      value={routine.schoolHours.start}
                      onChange={(e) => handleNestedChange('schoolHours', 'start', e.target.value)}
                      className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      School End Time
                    </label>
                    <input
                      type="time"
                      value={routine.schoolHours.end}
                      onChange={(e) => handleNestedChange('schoolHours', 'end', e.target.value)}
                      className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2">
                <FoodIcon className="w-5 h-5 text-orange-500" />
                Meal Times
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Breakfast
                  </label>
                  <input
                    type="time"
                    value={routine.mealTimes.breakfast}
                    onChange={(e) => handleNestedChange('mealTimes', 'breakfast', e.target.value)}
                    className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Lunch
                  </label>
                  <input
                    type="time"
                    value={routine.mealTimes.lunch}
                    onChange={(e) => handleNestedChange('mealTimes', 'lunch', e.target.value)}
                    className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Dinner
                  </label>
                  <input
                    type="time"
                    value={routine.mealTimes.dinner}
                    onChange={(e) => handleNestedChange('mealTimes', 'dinner', e.target.value)}
                    className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>
          </motion.div>
        );

      case 1:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">
                  When do you prefer to study?
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {['morning', 'afternoon', 'evening', 'night'].map((time) => (
                    <button
                      key={time}
                      onClick={() => {
                        const current = routine.preferredStudyTimes;
                        const updated = current.includes(time)
                          ? current.filter(t => t !== time)
                          : [...current, time];
                        handleInputChange('preferredStudyTimes', updated);
                      }}
                      className={`p-3 rounded-lg border-2 transition-all duration-200 ${
                        routine.preferredStudyTimes.includes(time)
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                          : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-blue-400 dark:hover:border-blue-500'
                      }`}
                    >
                      {time.charAt(0).toUpperCase() + time.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">
                  How long should study breaks be?
                </h3>
                <div className="flex items-center space-x-4">
                  <input
                    type="range"
                    min="5"
                    max="30"
                    step="5"
                    value={routine.studyBreaks}
                    onChange={(e) => handleInputChange('studyBreaks', parseInt(e.target.value))}
                    className="flex-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                  />
                  <span className="text-lg font-semibold text-gray-800 dark:text-gray-200 min-w-[60px]">
                    {routine.studyBreaks} min
                  </span>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">
                  What subjects are you studying?
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {['Mathematics', 'Science', 'English', 'History', 'Geography', 'Computer Science', 'Art', 'Music', 'Physical Education'].map((subject) => (
                    <button
                      key={subject}
                      onClick={() => {
                        const current = routine.subjects;
                        const updated = current.includes(subject)
                          ? current.filter(s => s !== subject)
                          : [...current, subject];
                        handleInputChange('subjects', updated);
                      }}
                      className={`p-3 rounded-lg border-2 transition-all duration-200 ${
                        routine.subjects.includes(subject)
                          ? 'border-green-500 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
                          : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-green-400 dark:hover:border-green-500'
                      }`}
                    >
                      {subject}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        );

      case 2:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2">
                    <PhoneIcon className="w-5 h-5 text-purple-500" />
                    Mobile Usage
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Hours per day</span>
                      <span className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                        {routine.mobileUsage}h
                      </span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="6"
                      step="0.5"
                      value={routine.mobileUsage}
                      onChange={(e) => handleInputChange('mobileUsage', parseFloat(e.target.value))}
                      className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2">
                    <TvIcon className="w-5 h-5 text-red-500" />
                    TV Time
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Hours per day</span>
                      <span className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                        {routine.tvTime}h
                      </span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="4"
                      step="0.5"
                      value={routine.tvTime}
                      onChange={(e) => handleInputChange('tvTime', parseFloat(e.target.value))}
                      className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2">
                    <FamilyIcon className="w-5 h-5 text-pink-500" />
                    Family Time
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Hours per day</span>
                      <span className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                        {routine.familyTime}h
                      </span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="3"
                      step="0.5"
                      value={routine.familyTime}
                      onChange={(e) => handleInputChange('familyTime', parseFloat(e.target.value))}
                      className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2">
                    <GamingIcon className="w-5 h-5 text-indigo-500" />
                    Gaming Time
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Hours per day</span>
                      <span className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                        {routine.gamingTime}h
                      </span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="3"
                      step="0.5"
                      value={routine.gamingTime}
                      onChange={(e) => handleInputChange('gamingTime', parseFloat(e.target.value))}
                      className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                    />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        );

      case 3:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-blue-800 dark:text-blue-200 mb-4">
                📋 Your Daily Routine Summary
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Wake up:</span>
                    <span className="font-semibold text-gray-800 dark:text-gray-200">{routine.wakeUpTime}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Sleep:</span>
                    <span className="font-semibold text-gray-800 dark:text-gray-200">{routine.sleepTime}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">School:</span>
                    <span className="font-semibold text-gray-800 dark:text-gray-200">{routine.schoolHours.start} - {routine.schoolHours.end}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Study breaks:</span>
                    <span className="font-semibold text-gray-800 dark:text-gray-200">{routine.studyBreaks} minutes</span>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Mobile usage:</span>
                    <span className="font-semibold text-gray-800 dark:text-gray-200">{routine.mobileUsage}h/day</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">TV time:</span>
                    <span className="font-semibold text-gray-800 dark:text-gray-200">{routine.tvTime}h/day</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Family time:</span>
                    <span className="font-semibold text-gray-800 dark:text-gray-200">{routine.familyTime}h/day</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Gaming:</span>
                    <span className="font-semibold text-gray-800 dark:text-gray-200">{routine.gamingTime}h/day</span>
                  </div>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-blue-200 dark:border-blue-700">
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Preferred study times:</span>
                  <span className="font-semibold text-gray-800 dark:text-gray-200">
                    {routine.preferredStudyTimes.join(', ')}
                  </span>
                </div>
                <div className="flex justify-between mt-2">
                  <span className="text-gray-600 dark:text-gray-400">Subjects:</span>
                  <span className="font-semibold text-gray-800 dark:text-gray-200">
                    {routine.subjects.length} selected
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700">
      {/* Header */}
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200">
              Create Your Study Routine
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Help us understand your daily schedule to create the perfect study plan
            </p>
          </div>
          {onBack && (
            <button
              onClick={onBack}
              className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors duration-200"
            >
              ← Back
            </button>
          )}
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-between mt-6">
          {steps.map((step, index) => (
            <div key={index} className="flex items-center">
              <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all duration-200 ${
                index <= currentStep
                  ? 'border-blue-500 bg-blue-500 text-white'
                  : 'border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-500'
              }`}>
                {index < currentStep ? (
                  <span className="text-sm">✓</span>
                ) : (
                  <span className="text-sm">{index + 1}</span>
                )}
              </div>
              {index < steps.length - 1 && (
                <div className={`w-16 h-0.5 mx-2 transition-all duration-200 ${
                  index < currentStep ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'
                }`} />
              )}
            </div>
          ))}
        </div>

        {/* Step Title */}
        <div className="mt-6">
          <div className="flex items-center gap-3">
            {steps[currentStep].icon}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                {steps[currentStep].title}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {steps[currentStep].description}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <AnimatePresence mode="wait">
          {renderStepContent()}
        </AnimatePresence>
      </div>

      {/* Footer */}
      <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-between">
        <button
          onClick={prevStep}
          disabled={currentStep === 0}
          className="px-6 py-3 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
        >
          Previous
        </button>
        
        <div className="flex gap-3">
          {currentStep === steps.length - 1 ? (
            <button
              onClick={handleComplete}
              className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all duration-200 transform hover:scale-105"
            >
              Create Study Plan
            </button>
          ) : (
            <button
              onClick={nextStep}
              className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all duration-200 transform hover:scale-105"
            >
              Next
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default RoutineCollection; 